// ==================================================================
// FILE: src/app/components/item-panel/item-panel.ts
// ==================================================================

import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Item, SelectionEvent } from './item-panel.model'; // Assuming you move interfaces to a model file

// --- NEW IMPORTS ---
import { ScrollingModule } from '@angular/cdk/scrolling';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-item-panel',
  standalone: true, // Make component standalone
  imports: [
    // Import modules needed for this component's template
    ScrollingModule,
    FormsModule,
  ],
  templateUrl: './item-panel.html',
  styleUrl: './item-panel.scss',
})
export class ItemPanel {
  @Input() title: string = '';
  @Input() items: Item[] = [];

  @Output() selectionChanged = new EventEmitter<SelectionEvent>();

  // --- LOGIC FOR SIMPLE LIST (<= 30 items) ---
  readonly INITIAL_DISPLAY_COUNT = 7;
  isExpanded = false;

  // --- LOGIC FOR ADVANCED LIST (> 30 items) ---
  readonly LONG_LIST_THRESHOLD = 30;
  searchTerm = '';

  /**
   * Checks if the list is long enough to warrant advanced UI features.
   * @returns {boolean}
   */
  get isLongList(): boolean {
    return this.items.length > this.LONG_LIST_THRESHOLD;
  }

  /**
   * Gets the items to be displayed in the template.
   * - For long lists, it filters by search term and sorts by selection status.
   * - For short lists, it handles the "see more/less" logic.
   * @returns {Item[]}
   */
  get displayedItems(): Item[] {
    if (this.isLongList) {
      const filtered = this.items.filter((item) =>
        item.label.toLowerCase().includes(this.searchTerm.toLowerCase())
      );

      // Sort to show selected items at the top
      return filtered.sort((a, b) => Number(b.selected ?? false) - Number(a.selected ?? false));
    } else {
      // Logic for simple list
      return this.isExpanded ? this.items : this.items.slice(0, this.INITIAL_DISPLAY_COUNT);
    }
  }

  toggleView(): void {
    this.isExpanded = !this.isExpanded;
  }

  onSelectionChange(changedItem: Item): void {
    changedItem.selected = !changedItem.selected;
    const eventPayload: SelectionEvent = {
      source: changedItem,
      selectedItems: this.getSelectedItems(),
    };
    this.selectionChanged.emit(eventPayload);
  }

  onClearAllClick(): void {
    // This action always clears all items, regardless of filtering
    this.items.forEach((item) => (item.selected = false));
    this.searchTerm = ''; // Also clear search
    const eventPayload: SelectionEvent = {
      source: null,
      selectedItems: this.getSelectedItems(),
    };
    this.selectionChanged.emit(eventPayload);
  }

  onSelectAllClick(): void {
    // For long lists, "Select All" applies only to the currently filtered items.
    // For short lists, it applies to all items.
    const itemsToSelect = this.isLongList ? this.displayedItems : this.items;
    itemsToSelect.forEach((item) => (item.selected = true));

    const eventPayload: SelectionEvent = {
      source: null,
      selectedItems: this.getSelectedItems(),
    };
    this.selectionChanged.emit(eventPayload);
  }

  getSelectedItems(): Item[] {
    return this.items.filter((item) => item.selected);
  }
}
